import CloseIcon from '@mui/icons-material/Close';
import RedeemOutlinedIcon from '@mui/icons-material/RedeemOutlined';
import { LoadingButton } from '@mui/lab';
import { Box, Card, CardContent, Chip, Divider, IconButton, Modal, TextField, Typography } from '@mui/material';
import { Confirm, Report } from 'notiflix';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';
import { giftMoves } from '../../data/api/incomes/IncomeRoutes';
import useIndex from '../../hooks/useIndex';
import validErrors from '../../hooks/validErrors';

/**
 * Modal para regalar jugadas a un Jugador desde el admin.
 * Props:
 *  - open: boolean
 *  - onClose: () => void
 *  - targetUser: { uuid, name, lastname, moves } — receptor del regalo
 *  - onSuccess: callback opcional con el payload del backend
 */
const GiftMovesModal = ({ open, onClose, targetUser, onSuccess }) => {
    const { token } = useIndex();
    const {
        register,
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm({ defaultValues: { moves: '' } });

    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (open) reset({ moves: '' });
    }, [open]);

    const handleClose = () => {
        if (submitting) return;
        reset();
        onClose?.();
    };

    const sendGift = async (moves) => {
        setSubmitting(true);
        try {
            const { data } = await giftMoves(token, {
                user_uuid: targetUser?.uuid,
                moves,
            });
            if (data?.success) {
                Report.success('Regalo enviado', data?.message ?? 'Jugadas regaladas correctamente.', 'Aceptar', { textAlign: 'center' });
                onSuccess?.(data?.data);
                onClose?.();
                reset();
            }
        } catch (error) {
            validErrors(error);
        } finally {
            setSubmitting(false);
        }
    };

    const onSubmit = (form) => {
        const moves = Number(form.moves);
        if (!Number.isInteger(moves) || moves < 1) return;

        Confirm.show(
            'Confirmar regalo',
            `¿Regalar ${moves} jugada(s) a ${targetUser?.name ?? ''} ${targetUser?.lastname ?? ''}?`,
            'Sí, regalar',
            'Cancelar',
            () => sendGift(moves),
            () => { },
        );
    };

    return (
        <Modal open={open} onClose={handleClose}>
            <Center>
                <Card sx={{ width: '100%', maxWidth: 480 }}>
                    <CardContent>
                        <Header>
                            <Box className="title-wrap">
                                <Box className="icon-box">
                                    <RedeemOutlinedIcon />
                                </Box>
                                <Typography variant="h6">Regalar jugadas</Typography>
                            </Box>
                            <IconButton onClick={handleClose} size="small">
                                <CloseIcon />
                            </IconButton>
                        </Header>

                        <Box sx={{ mb: 1 }}>
                            <Typography variant="caption" color="text.secondary">
                                Receptor
                            </Typography>
                            <Typography variant="subtitle2" sx={{ color: '#10285f', fontWeight: 900 }}>
                                {targetUser?.name} {targetUser?.lastname ?? ''}
                            </Typography>
                            <Chip
                                size="small"
                                label={`${Number(targetUser?.moves ?? 0)} jugadas actuales`}
                                sx={{ mt: 0.5 }}
                            />
                        </Box>

                        <Divider sx={{ my: 1.5 }} />

                        <form onSubmit={handleSubmit(onSubmit)} noValidate>
                            <TextField
                                fullWidth
                                size="small"
                                type="number"
                                label="Cantidad de jugadas a regalar"
                                inputProps={{ min: 1, step: 1 }}
                                /* Bloquea negativos / notación científica y evita
                                   que el scroll del mouse cambie el valor. */
                                onWheel={(e) => e.target.blur()}
                                onKeyDown={(e) => {
                                    if (['-', 'e', 'E', '+', '.', ','].includes(e.key)) {
                                        e.preventDefault();
                                    }
                                }}
                                error={!!errors.moves}
                                helperText={errors.moves?.message}
                                {...register('moves', {
                                    required: 'Indica una cantidad.',
                                    valueAsNumber: true,
                                    min: { value: 1, message: 'Debe ser al menos 1.' },
                                    validate: (v) => Number.isInteger(Number(v)) || 'Debe ser un número entero.',
                                })}
                            />

                            <LoadingButton
                                fullWidth
                                type="submit"
                                variant="contained"
                                loading={submitting}
                                startIcon={<RedeemOutlinedIcon />}
                                sx={{
                                    mt: 2,
                                    borderRadius: '12px',
                                    fontWeight: 900,
                                    textTransform: 'uppercase',
                                    background: 'linear-gradient(90deg, #10285f 0%, #1a2f5c 100%)',
                                    boxShadow: '0 6px 14px rgba(15, 34, 77, 0.18)',
                                    '&:hover': {
                                        background: 'linear-gradient(90deg, #1a2f5c 0%, #10285f 100%)',
                                    },
                                }}
                            >
                                <span>Regalar</span>
                            </LoadingButton>
                        </form>
                    </CardContent>
                </Card>
            </Center>
        </Modal>
    );
};

export default GiftMovesModal;

const Center = styled(Box)`
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 90%;
    max-width: 480px;
    outline: none;
`;

const Header = styled(Box)`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;

    .title-wrap {
        display: flex;
        align-items: center;
        gap: 0.7rem;
    }

    .icon-box {
        width: 38px;
        height: 38px;
        border-radius: 12px;
        background: rgba(214, 170, 58, 0.16);
        border: 1px solid rgba(214, 170, 58, 0.28);
        color: #10285f;
        display: flex;
        align-items: center;
        justify-content: center;
    }
`;
